'use client';

import { useEffect, useMemo, useState } from 'react';

import { cloneDeep, isEmpty } from 'lodash';
import { useParams, useSearchParams } from 'next/navigation';
import qs from 'qs';

import { categoriesEligibleForFilters } from '@/config/constants';
import { useAppContext } from '@/layouts/AppProvider';
import { useAppSelector } from '@/store';
import { Filter, Pet, QueryParam } from '@/types/ProductFIlters';

const initialFilters: Filter = {
  pet: {},
};

const useProductFilters = () => {
  const searchParams = useSearchParams();
  const param = useParams();

  const currentCategoryId = param?.category as string;

  const { recommendedForItems } = useAppContext();

  const [filters, setFilters] = useState<Filter>(initialFilters);

  const { totalNumberOfProductsInACategory, currentCategoryName, currentCategoryProductsCount } = useAppSelector(
    store => store.slices.CategoryProductStatsSlice
  );

  const isEligibleForFilters = categoriesEligibleForFilters.includes(currentCategoryId ?? '');
  const showFilters = isEligibleForFilters && (recommendedForItems || []).length > 0;

  const params = useMemo(
    () => qs.parse(decodeURIComponent(searchParams.toString()), { comma: true }) as QueryParam,
    [searchParams]
  );

  const itemCodes = useMemo(() => {
    return (
      recommendedForItems
        ?.filter(({ petProfileId }) => params?.pets?.includes(petProfileId?.toString()))
        ?.flatMap(pet => pet.itemCodes)
        ?.filter((itemCode, index, codes) => codes.indexOf(itemCode) === index) || []
    );
  }, [recommendedForItems, params]);

  const petNames = useMemo(() => {
    const petIds = Object.keys(filters?.pet) ?? [];
    return (
      recommendedForItems
        ?.filter(pet => petIds.includes(pet?.petProfileId?.toString()))
        .map(pet => pet.petProfileName) ?? []
    );
  }, [recommendedForItems, filters]);

  const { firstTwoPets, morePets, hasMorePets, hasPets } = useMemo(() => {
    const firstTwoPets = petNames?.slice(0, 2)?.join(', ');
    const morePets = petNames?.slice(2);
    const hasMorePets = morePets?.length > 0;
    const hasPets = petNames?.length > 0;

    return { firstTwoPets, morePets, hasMorePets, hasPets };
  }, [petNames]);

  const showTotalNumberOfProductsInACategory =
    params?.pets?.length &&
    totalNumberOfProductsInACategory != currentCategoryProductsCount &&
    showFilters &&
    itemCodes?.length > 0;

  useEffect(() => {
    if (!showFilters) return;

    const reformPetFilter = recommendedForItems
      ?.filter(pet => params?.pets?.includes(pet?.petProfileId?.toString()))
      .reduce((acc, curr) => {
        acc[curr.petProfileId] = curr.itemCodes;
        return acc;
      }, {} as Pet);

    setFilters(prevFilters => {
      const updatedFilters = cloneDeep(prevFilters);

      if (!isEmpty(reformPetFilter)) {
        updatedFilters.pet = reformPetFilter;
      } else {
        updatedFilters.pet = {} as Pet;
      }

      return updatedFilters;
    });
  }, [params, recommendedForItems, showFilters]);

  return {
    totalNumberOfProductsInACategory,
    currentCategoryName,
    currentCategoryId,
    currentCategoryProductsCount,
    showTotalNumberOfProductsInACategory,
    showFilters,
    firstTwoPets,
    morePets,
    hasMorePets,
    hasPets,
    itemCodes,
  };
};

export default useProductFilters;
